import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { it } from 'date-fns/locale';

export function formatDate(date: string | Date, pattern = 'dd/MM/yyyy'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return format(d, pattern, { locale: it });
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return format(d, "dd/MM/yyyy 'alle' HH:mm", { locale: it });
}

export function formatTime(time: string): string {
  // Assumes HH:mm:ss or HH:mm format
  const parts = time.split(':');
  return `${parts[0]}:${parts[1]}`;
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(d)) return '-';
  return formatDistanceToNow(d, { addSuffix: true, locale: it });
}

export function formatCompanySize(size: string): string {
  const labels: Record<string, string> = {
    '1-10': '1-10 dipendenti',
    '11-50': '11-50 dipendenti',
    '51-200': '51-200 dipendenti',
    '201-500': '201-500 dipendenti',
    '500+': '500+ dipendenti',
  };
  return labels[size] || size;
}

export function formatTimeline(timeline: string): string {
  const labels: Record<string, string> = {
    'immediate': 'Immediato',
    '1-3_months': '1-3 mesi',
    '3-6_months': '3-6 mesi',
    '6+_months': '6+ mesi',
  };
  return labels[timeline] || timeline;
}

export function formatBudget(budget: string): string {
  const labels: Record<string, string> = {
    '<5k': 'Meno di 5.000',
    '5-15k': '5.000 - 15.000',
    '15-50k': '15.000 - 50.000',
    '50k+': 'Oltre 50.000',
  };
  return labels[budget] || budget;
}

export function formatStatus(status: string): string {
  const labels: Record<string, string> = {
    'new': 'Nuovo',
    'contacted': 'Contattato',
    'qualified': 'Qualificato',
    'opportunity': 'Opportunita',
    'won': 'Vinto',
    'lost': 'Perso',
    'active': 'Attiva',
    'completed': 'Completata',
    'abandoned': 'Abbandonata',
    'confirmed': 'Confermato',
    'cancelled': 'Cancellato',
    'no_show': 'Non presentato',
  };
  return labels[status] || status;
}

export function formatMeetingType(type: string): string {
  const labels: Record<string, string> = {
    'discovery': 'Discovery Call',
    'demo': 'Demo Prodotto',
    'technical': 'Approfondimento Tecnico',
  };
  return labels[type] || type;
}

export function formatNumber(num: number, locale = 'it-IT'): string {
  return new Intl.NumberFormat(locale).format(num);
}

export function formatPercentage(num: number, decimals = 1): string {
  return `${num.toFixed(decimals)}%`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minuti`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours} ${hours === 1 ? 'ora' : 'ore'}`;
  }
  return `${hours}h ${mins}m`;
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[àáâã]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõ]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
